import { useQuery } from "@tanstack/react-query";
import type { DashboardMode } from "@nexus/contracts";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { writeActivePresetId } from "@/features/dashboard/lib/active-preset-storage";
import { buildCurrentDrilldownPresetPayload } from "@/features/drilldown/lib/filter-preset-utils";
import {
  drilldownFilterPresetsQueryOptions,
  useCreateDrilldownFilterPreset,
} from "@/lib/queries/filter-presets.queries";
import { SaveFilterPresetControl } from "./SaveFilterPresetControl";

interface DrilldownSaveFilterPresetProps {
  mode: DashboardMode;
  canSavePreset?: boolean;
  onPresetCreated?: (presetId: string) => void;
}

export function DrilldownSaveFilterPreset({
  mode,
  canSavePreset = true,
  onPresetCreated,
}: DrilldownSaveFilterPresetProps) {
  const canListPresets = useIamPermission(usePermissions().DRILLDOWN_VIEW_PAGE);
  const createMutation = useCreateDrilldownFilterPreset(mode);
  const { data: presets = [] } = useQuery({
    ...drilldownFilterPresetsQueryOptions(mode),
    enabled: canListPresets,
  });

  if (!canListPresets) return null;

  return (
    <SaveFilterPresetControl
      disabled={!canSavePreset}
      presets={presets}
      isPending={createMutation.isPending}
      i18nPrefix="drilldown.filterPresets"
      onSave={async (name) => {
        const payload = buildCurrentDrilldownPresetPayload(mode);
        const created = await createMutation.mutateAsync({
          name,
          ...payload,
        });
        writeActivePresetId("drilldown", mode, created.id);
        onPresetCreated?.(created.id);
      }}
    />
  );
}
